/**
 * Format an ISO date (YYYY-MM-DD or full ISO 8601) as DD/MM/YYYY for
 * read-only display. Returns '—' for null/undefined/invalid values so
 * the operator never sees a raw "Invalid Date".
 *
 * Edit forms use the native `<input type="date">` whose visible format
 * is browser-locale-dependent; this helper guarantees a single
 * European display format on every read-only surface (KYC tab, person
 * lists, audit log).
 */
export function formatDateDDMMYYYY(value: string | null | undefined): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (!trimmed) return '—';
  const isoDate = trimmed.includes('T') ? trimmed.slice(0, trimmed.indexOf('T')) : trimmed;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return '—';
  const [, y, m, d] = match;
  return `${d}/${m}/${y}`;
}

/**
 * Compute the latest YYYY-MM-DD a date-of-birth input should accept so
 * the operator cannot enter an under-18 birthdate. Returned in the
 * exact format expected by `<input type="date" max>` (locale-stable).
 */
export function maxAdultBirthDate(today: Date = new Date()): string {
  const ref = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  const y = ref.getFullYear();
  const m = String(ref.getMonth() + 1).padStart(2, '0');
  const d = String(ref.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Today's date in the YYYY-MM-DD shape expected by `<input type="date" max>`.
 * Use as the `max` for incorporation/registration dates so the operator
 * cannot pick a future date.
 */
export function todayIso(today: Date = new Date()): string {
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
